import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BABY_AMBIENTS, BABY_DURATIONS } from "@/lib/sessions";
import { store } from "@/lib/storage";
import { playAmbient, stopAmbient, setVolume, isAudioSupported } from "@/lib/audio";
import { ChevronLeft, Play, Square, Volume2, X } from "lucide-react";

function fmt(sec) {
  sec = Math.max(0, Math.floor(sec));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function BabyAmbient() {
  const nav = useNavigate();
  const [ambient, setAmbient] = useState("pink");
  const [minutes, setMinutes] = useState(10);
  const [playing, setPlaying] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [vol, setVol] = useState(0.4);
  const [error, setError] = useState(null);
  const tickRef = useRef(null);

  useEffect(() => () => { stopAmbient({ fade: false }); if (tickRef.current) clearInterval(tickRef.current); }, []);

  async function start() {
    if (!isAudioSupported()) { setError("Audio non disponible dans ce navigateur."); return; }
    const res = await playAmbient(ambient, vol);
    if (!res.ok) { setError("Impossible de démarrer l'audio."); return; }
    setError(null);
    setPlaying(true);
    setRemaining(minutes * 60);
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { stopAll(true); return 0; }
        if (r === 20) stopAmbient({ fade: true, fadeSeconds: 20 });
        return r - 1;
      });
    }, 1000);
  }

  function stopAll(natural = false) {
    if (!natural) stopAmbient({ fade: true, fadeSeconds: 3 });
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setPlaying(false);
    setRemaining(0);
  }

  function onVol(v) { setVol(v); setVolume(v); }

  if (playing) {
    return (
      <div className="fixed inset-0 z-50 bg-[color:var(--bg-deep)] flex flex-col" data-testid="baby-player">
        <div className="flex items-center justify-between px-5 pt-6 safe-top">
          <button
            data-testid="baby-player-close"
            onClick={() => { stopAll(false); nav(-1); }}
            className="w-10 h-10 rounded-full border border-[color:var(--border)] flex items-center justify-center text-[color:var(--ivoire)]"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
          <p className="font-mono text-[11px] tracking-widest uppercase text-[color:var(--sauge-light)]">
            Mode bébé · sons uniquement
          </p>
          <div className="w-10" />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <p className="text-[color:var(--ivoire-muted)] text-sm">Ambiance</p>
          <h2 className="font-serif text-3xl text-[color:var(--ivoire)] mt-1">{BABY_AMBIENTS.find(a => a.id === ambient)?.label}</h2>
          <div className="mt-10 font-mono text-5xl text-[color:var(--ivoire)]" data-testid="baby-remaining">{fmt(remaining)}</div>
          <p className="mt-3 text-xs text-[color:var(--ivoire-muted)]">Arrêt progressif à la fin.</p>

          <button
            data-testid="baby-stop-btn"
            onClick={() => stopAll(false)}
            className="mt-10 w-16 h-16 rounded-full border border-[color:var(--border)] text-[color:var(--ivoire)] flex items-center justify-center"
            aria-label="Arrêter"
          >
            <Square size={20} />
          </button>

          <div className="w-full max-w-xs mt-8 flex items-center gap-3">
            <Volume2 size={16} className="text-[color:var(--ivoire-muted)]" />
            <input
              data-testid="baby-volume"
              type="range" min="0" max="1" step="0.01"
              value={vol}
              onChange={(e) => onVol(parseFloat(e.target.value))}
              className="flex-1 accent-[color:var(--sauge)]"
              aria-label="Volume"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in pt-6">
      <button data-testid="back-btn" onClick={() => nav(-1)} className="flex items-center gap-1 text-[color:var(--ivoire-muted)] text-sm mb-4">
        <ChevronLeft size={16} /> Retour
      </button>
      <h1 className="font-serif text-3xl text-[color:var(--ivoire)]">Ambiance pour bébé</h1>
      <p className="text-sm text-[color:var(--ivoire-muted)] mt-2">Sons uniquement. La lecture démarre à la demande.</p>

      <section className="mt-8">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-3">Ambiance</p>
        <div className="space-y-2">
          {BABY_AMBIENTS.map(a => (
            <button
              key={a.id}
              data-testid={`baby-ambient-${a.id}`}
              onClick={() => setAmbient(a.id)}
              className={`w-full text-left rounded-xl border px-4 py-3 transition ${
                ambient === a.id ? "border-[color:var(--sauge)] bg-[color:var(--sauge)]/15" : "border-[color:var(--border)] bg-[#142235]/60"
              }`}
            >
              <div className="font-medium text-[color:var(--ivoire)]">{a.label}</div>
              <div className="text-xs text-[color:var(--ivoire-muted)]">{a.description}</div>
            </button>
          ))}
        </div>
      </section>

      <section className="mt-6">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-3">Minuteur</p>
        <div className="flex gap-2">
          {BABY_DURATIONS.map(d => (
            <button
              key={d}
              data-testid={`baby-duration-${d}`}
              onClick={() => setMinutes(d)}
              className={`px-5 py-3 rounded-xl border font-mono text-sm ${
                minutes === d ? "bg-[color:var(--sauge)] text-[color:var(--bg-deep)] border-[color:var(--sauge)]" : "border-[color:var(--border)] bg-[#142235]/60 text-[color:var(--ivoire)]"
              }`}
            >{d} min</button>
          ))}
        </div>
      </section>

      <section className="mt-6">
        <p className="font-mono text-[10px] tracking-widest uppercase text-[color:var(--sauge-light)] mb-3">Volume initial</p>
        <input
          data-testid="baby-initial-volume"
          type="range" min="0" max="0.7" step="0.01"
          value={vol}
          onChange={(e) => setVol(parseFloat(e.target.value))}
          className="w-full accent-[color:var(--sauge)]"
        />
      </section>

      {error && <p data-testid="baby-error" className="mt-4 text-sm text-amber-400">{error}</p>}

      <button
        data-testid="baby-play-btn"
        onClick={start}
        className="mt-10 w-full h-14 rounded-2xl bg-[color:var(--sauge)] text-[color:var(--bg-deep)] font-medium flex items-center justify-center gap-2"
      >
        <Play size={18} fill="currentColor" /> Démarrer
      </button>
    </div>
  );
}
